import { apiPostAsync } from '../common/service-utils'
import * as constants from '../common/constants'
import * as errorCodes from '../common/errorCodes'
import * as JspUtil from '../common/JspUtil'
import { hashHistory } from '../common/navigationService';
import * as appActions from '../layout/actionCreators'
import * as tfaActions from '../tfapages/actionCreators'
import { runConnectivityProbeThenReport } from './connectivityProbe'
import {
    USER_INPUT_USER_NAME_CHANGE, USER_INPUT_APP_KEY_CHANGE, USER_INPUT_PASSWORD_CHANGE,
    USER_INPUT_PASSWORD_LESS_OTP_CHANGE, USER_INPUT_CAPTCHA_RESPONSE, PASSWORD_LESS_SAML_CONFIG,
    USER_INPUT_TFA_PASSCODE_CHANGE, USER_INPUT_TFA_DELIVERY_PREFERENCE_CHANGE,
    AUTH_SESSION, TFA_STATE, EULA_STATE, AUTH_MODE_STATE, SAVED_AUTH_PARAM,
    AUTHENTICATED_USER, AUTHENTICATION_PARAMETERS, REFERENCE_DATA,
    GLOBAL_ID_TRANSITION, GLOBAL_ID_TRANSITION_CLEAR,
    CONTINUE_WITH_APPID_CLICK, ERR_MSG, PASSWORD_EXPIRED, MAX_SESSIONS_EXCEED,
} from './actionTypes';

export * from './actionTypes';


// --- authenticateUser: decomposed into async + error handler ---

export async function authenticateUserAsync(params) {
    let { userName, appKey, loginApp, password, passwordLessOtp, captchaResponse, tfaPasscode,
        mersOrganization, rememberMe, webauthnCredential, useRegisteredPasskey, appUserLoginTfa,
        checkSession, continueLoginWithAppId } = params;

    if (mersOrganization && userName.indexOf("@") == -1) {
        userName = mersOrganization + "_" + userName;
    }
    if (userName && userName.indexOf("@") !== -1) {
        appKey = loginApp;
    }
    let permissions = appKey;
    if (JspUtil.isValueAvailable(window.jspPermissions)) permissions = window.jspPermissions;

    const postData = {
        userId: userName, globalId: userName, password, passwordLessOtp, captchaResponse,
        appKey, rememberUserId: rememberMe ? 'Y' : 'N', permissions: permissions + ',launcher',
    };
    if (tfaPasscode) postData.otpCode = tfaPasscode;
    if (continueLoginWithAppId === 'Y') postData.transitionToGlobalIdContinueLoginWithAppId = 'Y';
    if (JspUtil.isValueAvailable(window.jspSamlSession)) postData.samlSession = window.jspSamlSession;
    if (JspUtil.isValueAvailable(window.jspSecondAuth)) postData.secondAuth = window.jspSecondAuth;
    if (webauthnCredential) {
        postData.webauthnRequestId = webauthnCredential.requestId;
        postData.webauthnPublicKey = webauthnCredential.publicKey;
        postData.webauthnUserName = webauthnCredential.userName;
        postData.webauthnApp = webauthnCredential.appKey;
    }
    if (useRegisteredPasskey) postData.useRegisteredPasskey = useRegisteredPasskey;
    if (checkSession) postData.checkSession = checkSession;

    // Pick endpoint
    let apiUrl = constants.API_AUTHENTICATE_TFA;
    let isLauncher = false;
    if (appUserLoginTfa === "True" && useRegisteredPasskey) {
        apiUrl = constants.WEBAUTHN_GET_TFA_REQUEST;
    } else if (loginApp === constants.APP_LAUNCHER || permissions.includes(constants.APP_LAUNCHER)) {
        postData.appKey = "ANY";
        isLauncher = true;
        apiUrl = constants.API_AUTHENTICATE_LAUNCHER;
    } else if (appKey === constants.APP_KEY_GLOBAL) {
        apiUrl = constants.API_AUTHENTICATE_GLOBAL;
    }

    const resp = await apiPostAsync(apiUrl, null, postData);
    await probeConnectivityIfConfigured(resp);
    return { resp, permissions, isLauncher, postData };
}

// Lives here, not in the authenticateUser thunk: LoginTemplate calls authenticateUserAsync
// directly, so this is the only point every login path passes through.
async function probeConnectivityIfConfigured(resp) {
    try {
        const connectivityTest = resp && resp.data && resp.data.connectivityTest;
        if (connectivityTest && connectivityTest.urls && connectivityTest.urls.length) {
            await runConnectivityProbeThenReport(connectivityTest);
        }
    } catch (e) {
        // Probe/report failure must never fail login.
    }
}

function dispatchAuthUserApp(dispatch, resp) {
    const authUserName = resp.data.authenticatedUser || resp.data.user;
    const authApp = resp.data.authenticatedApp || resp.data.app;
    if (authApp) dispatch({ type: USER_INPUT_APP_KEY_CHANGE, data: authApp });
    if (authUserName) dispatch({ type: USER_INPUT_USER_NAME_CHANGE, data: authUserName });
    return { authUserName, authApp };
}

export function handleAuthError(dispatch, resp, params) {
    const { password, permissions, postData, authParam } = params;
    const code = resp.code;

    // Password errors → change password page
    if (code == errorCodes.ERR_PASSWORD_EXPIRED || code == errorCodes.ERR_WEAK_PASSWORD) {
        dispatchAuthUserApp(dispatch, resp);
        dispatch({ type: AUTH_SESSION, data: { authenticatedApp: resp.data.authenticatedApp } });
        dispatch({ type: AUTHENTICATED_USER, data: resp.data.authenticatedUser });
        dispatch({ type: ERR_MSG, data: resp.message });
        hashHistory.push(constants.ROUTE_PAGE_CHANGE_PASSWORD);
        return;
    }

    // Temp password expired → forgot password page
    if (code == errorCodes.ERR_APIUSER_TEMP_PASSWORD_EXPIRED || code == errorCodes.ERR_TEMP_PASSWORD_EXPIRED) {
        dispatch({ type: PASSWORD_EXPIRED, data: true });
        const state = { pageMessage: resp.message };
        if (code == errorCodes.ERR_APIUSER_TEMP_PASSWORD_EXPIRED) state.isApiUser = true;
        hashHistory.push({ pathname: constants.ROUTE_PAGE_FORGOT_PASSWORD, state });
        return;
    }

    // Session limits
    if (code == errorCodes.ERR_USER_EXCEED_MAX_SESSIONS) {
        dispatch({ type: MAX_SESSIONS_EXCEED, data: { userId: postData.userId, password, appKey: postData.appKey, permissions } });
        hashHistory.push(constants.ROUTE_PAGE_MAX_SESSIONS_EXCEED);
        return;
    }
    if (code == errorCodes.ERR_USER_HAS_EXISTING_SESSIONS) {
        dispatch({ type: AUTHENTICATION_PARAMETERS, data: { userId: postData.userId, password, appKey: postData.appKey, permissions, authParam } });
        hashHistory.push(constants.ROUTE_PAGE_USER_HAS_EXISTING_SESSIONS);
        return;
    }

    // TFA missing info or non-confirmed global ID → register TFA
    if (code == errorCodes.ERR_TFA_MISSING_INFO || code == errorCodes.ERR_NON_CONFIRMED_GLOBAL_ID) {
        const { authUserName, authApp } = dispatchAuthUserApp(dispatch, resp);
        dispatch(tfaActions.getTfaDetails(false, authUserName, authApp, password));
        hashHistory.push(constants.ROUTE_PAGE_TFA_EMAIL_REGISTER);
        return;
    }

    // TFA required → show TFA form on login page
    if (code == errorCodes.ERR_REQUIRES_TFA) {
        const tfaData = { required: true };
        dispatchAuthUserApp(dispatch, resp);
        if (resp.data) {
            if (resp.data.userMessage) tfaData.prompt = resp.data.userMessage;
            tfaData.isWebAuthnRegistered = resp.data.isWebAuthnRegistered === 'Y' ? 'Y' : 'N';
            tfaData.isTfaDeviceRegistered = resp.data.isTfaDeviceRegistered === 'Y' ? 'Y' : 'N';
            dispatch({ type: USER_INPUT_TFA_DELIVERY_PREFERENCE_CHANGE, data: resp.data.tfaDeliveryPref });
            if (resp.data.webauthn) {
                tfaData.webAuthnRequest = resp.data.webauthn;
                dispatch(tfaActions.loginWebAuthn(authParam, JSON.parse(resp.data.webauthn)));
            }
        }
        dispatch({ type: TFA_STATE, data: tfaData });
        return;
    }

    // EULA required
    if (code == errorCodes.ERR_EULA_REQUIRED) {
        const eulaDoc = {
            eulaUrl: resp.data.url, content: resp.data.eulaContent, user: resp.data.userId,
            password, passwordLessOtp: params.passwordLessOtp, captchaResponse: params.captchaResponse,
            app: resp.data.appKey, otpCode: params.tfaPasscode, permissions
        };
        if (JspUtil.isValueAvailable(window.jspPermissions)) eulaDoc.jspPermissions = window.jspPermissions;
        dispatch({ type: EULA_STATE, data: { eulaDoc } });
        openEulaPage(eulaDoc, dispatch);
        return;
    }

    // Global ID transition errors
    if (code == errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_PRESENT ||
        code == errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_PRESENT_NOT_VERIFIED ||
        code == errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_NOT_PRESENT_APP_ID_2FA_VERIFIED ||
        code == errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_NOT_PRESENT_APP_ID_2FA_VERIFIED_LINK_FAILED ||
        code == errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_NOT_PRESENT_APP_ID_2FA_NOT_ENABLED) {
        const transData = { token: { loginUserId: postData.userId, loginApp: postData.appKey, password } };
        if (resp.data) {
            // Login may have used ANY; keep the app the server resolved so Register
            // Global ID can send a real app key.
            if (resp.data.appKey) dispatch({ type: USER_INPUT_APP_KEY_CHANGE, data: resp.data.appKey });
            // Compare against null/undefined, not truthiness: "0" is the value that
            // drives the zero-attempts flow and would otherwise be dropped here.
            if (resp.data.remainingAppIdLoginAttempts != null) transData.remainingAttempts = resp.data.remainingAppIdLoginAttempts;
            if (resp.data.email) transData.email = resp.data.email;
            // Show the server-resolved message (StringConstants -> Admin override ->
            // sso-messages) so SSO-Admin can override the zero-attempts text.
            if (loginTransitionGlobal(dispatch, transData)) return resp.message;
        }
        if (code == errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_NOT_PRESENT_APP_ID_2FA_NOT_ENABLED) {
            if (resp.data) {
                if (resp.data.userId) dispatch({ type: USER_INPUT_USER_NAME_CHANGE, data: resp.data.userId });
                if (resp.data.remainingAppIdLoginAttempts != null) transData.remainingAttempts = resp.data.remainingAppIdLoginAttempts;
            }
            transData.errNoGlobal = true;
        } else {
            transData.errTransition = true;
        }
        dispatch({ type: GLOBAL_ID_TRANSITION, data: transData });
        return resp.message;
    }
    if (code == errorCodes.ERR_TRANSITION_TO_GLOBAL_GLOBAL_ID_PRESENT_APP_ID_ATTEMPTS_EXCEEDED) {
        dispatch({ type: GLOBAL_ID_TRANSITION, data: { errAttemptsExceeded: true } });
        return resp.message;
    }

    // Passwordless
    if (code == errorCodes.ERR_PASSWORD_LESS_REQUIRES_SAML_REDIRECT) { handlePasswordLessSAMLResp(dispatch, resp); return; }
    if (code == errorCodes.ERR_PASSWORD_LESS_REQUIRES_OTP) { handlePasswordLessOTPResp(dispatch); return; }

    // Alert-only errors (no navigation) — return message for component to display
    const alertMessages = {
        [errorCodes.ERR_ACCOUNT_LOCKED]: JspUtil.getLabelFromJspVariables('note_account_locked'),
        [errorCodes.ERR_2FA_IS_DISABLED]: JspUtil.getLabelFromJspVariables('err_2fa_is_disabled'),
        [errorCodes.ERR_2FA_EMAIL_INVALID]: JspUtil.getLabelFromJspVariables('err_2fa_email_invalid'),
        [errorCodes.ERR_2FA_NOT_REGISTERED]: JspUtil.getLabelFromJspVariables('err_2fa_not_registered'),
        [errorCodes.ERR_GENERAL]: JspUtil.getLabelFromJspVariables('err_invalid_credentials'),
    };
    if (alertMessages[code]) return alertMessages[code];

    // TFA setup incomplete, OTP already used — no-op or default
    if (code == errorCodes.ERR_TFA_SETUP_INCOMPLETE) {
        dispatchAuthUserApp(dispatch, resp);
        return null;
    }

    // Default
    handlePasswordRequiredResp(dispatch);
    return resp.message;
}

export function authenticateUser(userName, appKey, loginApp, password, passwordLessOtp, captchaResponse, tfaPasscode, mersOrganization, rememberMe, webauthnCredential, useRegisteredPasskey, appUserLoginTfa, checkSession) {
    return function (dispatch, getState) {
        const authParam = saveAuthParam(userName, appKey, loginApp, password, passwordLessOtp, captchaResponse, tfaPasscode, mersOrganization, rememberMe, webauthnCredential, useRegisteredPasskey, appUserLoginTfa);
        dispatch({ type: SAVED_AUTH_PARAM, data: authParam });
        dispatch({ type: AUTH_SESSION, data: { isAuthenticated: false } });

        const continueLoginWithAppId = getState().userInputsReducer.continueLoginWithAppId;
        const startTime = Date.now();

        authenticateUserAsync({
            userName, appKey, loginApp, password, passwordLessOtp, captchaResponse, tfaPasscode,
            mersOrganization, rememberMe, webauthnCredential, useRegisteredPasskey, appUserLoginTfa,
            checkSession, continueLoginWithAppId
        }).then(({ resp, permissions, isLauncher }) => {
            const authData = { authStart: startTime, authEnd: Date.now(), ...getState().authenticatedUser, ...resp.data };
            const connTest = getState().authenticateReducer.connectionTestState || {};
            dispatch(logUserConnection(connTest.settings, permissions, authData, connTest.results));
            dispatch({ type: AUTH_SESSION, data: {
                authenticatedUser: authData,
                isAuthenticated: true,
                authenticatedApp: resp.data.authenticatedApp,
                isLauncherAuth: !!isLauncher,
            }});
            if (resp.data && resp.data.addressStatus === 'P') {
                hashHistory.push({ pathname: constants.ROUTE_PAGE_CONFIRM_CONTACT, state: { sourcePage: constants.ROUTE_PAGE_AUTHENTICATED } });
            } else {
                hashHistory.push(constants.ROUTE_PAGE_AUTHENTICATED);
            }
        }).catch(resp => {
            dispatch({ type: USER_INPUT_TFA_PASSCODE_CHANGE, data: '' });
            // iceIM fallback
            if (JspUtil.isValueAvailable(window.jspPermissions) && window.jspPermissions === 'iceIM' && appKey === 'ICE') {
                if (resp.code == errorCodes.ERR_GENERAL || resp.code == errorCodes.ERR_ACCOUNT_LOCKED) {
                    dispatch({ type: USER_INPUT_APP_KEY_CHANGE, data: appActions.APP_KEY_YJ });
                    dispatch(authenticateUser(userName, appActions.APP_KEY_YJ, "iceIM", password, passwordLessOtp, captchaResponse, tfaPasscode, mersOrganization, rememberMe));
                    return;
                }
            }
            handleAuthError(dispatch, resp, {
                password, permissions: appKey,
                postData: { userId: userName, appKey }, authParam,
                passwordLessOtp, captchaResponse, tfaPasscode
            });
        });
    }
}

function buildAuthData(getState, data) {
    return {
        ...getState().authenticatedUser,
        user: data.displayUser,
        app: data.displayApp,
        displayUser: data.displayUser,
        displayApp: data.displayApp,
        authenticatedUser: data.displayUser,
        authenticatedApp: data.displayApp,
        links: data.links,
        passwordExpiration: data.passwordExpiration,
        roles: data.roles,
        apps: data.apps,
        globalId: data.globalId
    };
}

function isAlreadyAuthenticated(data, permissionKey) {
    if (data && data.permissionChecks) {
        return data.permissionChecks.some(p => p && (p.permissions === permissionKey || p.permissions === 'launcher') && p.code === '0');
    }
    return false;
}

export function checkPermissions(pageToGo, options = {}) {
    return function (dispatch, getState) {
        const {
            navigateOnSuccess = true,
            navigateOnFailure = true,
        } = options;
        return apiPostAsync(constants.API_CHECK_PERMISSIONS, null, {
            token: '',
            permissionChecks: [{ permissions: 'launcher' }]
        }).then(resp => {
            const data = resp.data;
            if (isAlreadyAuthenticated(data, 'launcher')) {
                dispatch({ type: AUTH_SESSION, data: { authenticatedUser: buildAuthData(getState, data), isAuthenticated: true } });
                dispatch(getReferenceData(data.displayUser, data.displayApp));
                if (pageToGo) {
                    loadUserProfile(data.displayUser, data.displayApp).then(profileResp => {
                        if (pageToGo.confirmed) pageToGo.confirmed(profileResp.data);
                    }).catch(err => console.error('loadUserProfile failed:', err));
                    return;
                }
                if (navigateOnSuccess) {
                    hashHistory.push(constants.ROUTE_PAGE_AUTHENTICATED);
                }
            }
        }).catch(resp => {
            if (resp.code == errorCodes.ERR_PLEASE_LOGIN) {
                dispatch(logoutUserToken("", true, false));
            }
            dispatch({ type: AUTH_SESSION, data: { isAuthenticated: false } });
            if (navigateOnFailure) {
                hashHistory.push(constants.ROUTE_PAGE_LOGIN);
            }
        });
    }
}

export function checkFederatedPermissions(appKey) {
    return function (dispatch, getState) {
        const permKey = (appKey && appKey !== 'undefined' && appKey.trim() !== '') ? appKey : 'launcher';
        return apiPostAsync(constants.API_CHECK_FEDERATED_PERMISSIONS, null, {
            token: '',
            permissionChecks: [{ permissions: permKey }]
        }).then(resp => {
            const data = resp.data;
            if (isAlreadyAuthenticated(data, permKey)) {
                dispatch({ type: AUTH_SESSION, data: { authenticatedUser: buildAuthData(getState, data), isAuthenticated: true } });
                dispatch(getReferenceData(data.displayUser, data.displayApp));
                hashHistory.push(constants.ROUTE_PAGE_AUTHENTICATED);
            }
        }).catch(resp => {
            if (resp.code == errorCodes.ERR_PLEASE_LOGIN) {
                dispatch(logoutUserToken("", true, false));
            }
            dispatch({ type: AUTH_SESSION, data: { isAuthenticated: false } });
            hashHistory.push(constants.ROUTE_PAGE_LOGIN);
        });
    }
}


export async function acceptEulaDoc(eulaDoc) {
    return apiPostAsync("api/acceptEula", null, {
        userName: eulaDoc.user,
        appKey: eulaDoc.app,
        eulaUrl: eulaDoc.eulaUrl
    });
}

export function getAcceptEulaUrl(eulaDoc, isLauncher) {
    let url = constants.API_AUTHENTICATE_TFA;
    const postData = {
        userId: eulaDoc.user,
        password: eulaDoc.password,
        appKey: eulaDoc.app,
        otpCode: eulaDoc.otpCode,
        passwordLessOtp: eulaDoc.passwordLessOtp,
        captchaResponse: eulaDoc.captchaResponse,
        acceptEulaUrl: eulaDoc.eulaUrl,
        permissions: eulaDoc.permissions,
    };
    if (eulaDoc.app === 'GLOBAL') {
        url = "api/authenticateGlobalId";
        postData.globalId = eulaDoc.user;
        postData.permissions = "GLOBAL,launcher";
    }
    if (isLauncher) {
        url = "api/authenticateLauncher";
        postData.permissions = "LAUNCHER,launcher";
    }
    if (eulaDoc.jspPermissions) {
        postData.permissions = eulaDoc.jspPermissions + ',launcher';
    }
    return { url, postData };
}

export async function acceptEula(eulaDoc, isLauncher) {
    const { url, postData } = getAcceptEulaUrl(eulaDoc, isLauncher);
    return apiPostAsync(url, null, postData);
}

export function logoutUserSessions(userId, password, appKey, permissions) {
    return function (dispatch, getState) {
        if (getState().authenticateReducer.authSession.authenticatedUser && getState().authenticateReducer.authSession.authenticatedUser.user) {
            userId = getState().authenticateReducer.authSession.authenticatedUser.user;
        }
        // Email-based logins are Global ID users. The per-app password row may be stale after a
        // Global ID password change — send GLOBAL so the server validates against the
        // authoritative Global ID password (same fix as authAndSendCodeTo2FAEmail).
        if (userId && userId.indexOf('@') !== -1) {
            appKey = constants.APP_KEY_GLOBAL;
        }
        dispatch({ type: TFA_STATE, data: { required: false } });
        dispatch({ type: GLOBAL_ID_TRANSITION, data: GLOBAL_ID_TRANSITION_CLEAR });
        dispatch({ type: 'USER_CONTACT_VERIFIED', data: false });
        dispatch({ type: 'USER_PROFILE', data: {} });
        dispatch({ type: CONTINUE_WITH_APPID_CLICK, data: '' });
        apiPostAsync(constants.API_LOGOUT_USERSESSIONS, null, {
            userId, appKey, password, token: '', permissions
        }).then(() => {
            dispatch({ type: AUTH_SESSION, data: { authenticatedUser: {}, isAuthenticated: false } });
            dispatch({ type: USER_INPUT_PASSWORD_CHANGE, data: '' });
            dispatch({ type: USER_INPUT_TFA_PASSCODE_CHANGE, data: '' });
            hashHistory.push(constants.ROUTE_PAGE_LOGIN);
        }).catch(() => {
            hashHistory.push(constants.ROUTE_PAGE_LOGIN);
        });
    }
}

export function logoutUserToken(userId, stayPage, doClear, logoutParentToken) {
    return function (dispatch, getState) {
        const currentUserName = getState().userInputsReducer.userName || (getState().authenticateReducer.authSession.authenticatedUser && getState().authenticateReducer.authSession.authenticatedUser.user) || '';
        const rememberMeValue = getState().userInputsReducer.rememberMe;
        if (getState().authenticateReducer.authSession.authenticatedUser && getState().authenticateReducer.authSession.authenticatedUser.user) {
            userId = getState().authenticateReducer.authSession.authenticatedUser.user;
        }
        const postData = { userId, token: '', logoutParentToken: logoutParentToken || 'Y' };

        const clearAuthAndRedirect = () => {
            dispatch({ type: TFA_STATE, data: { required: false } });
            dispatch({ type: AUTH_MODE_STATE, data: { requiresPassword: false } });
            dispatch({ type: GLOBAL_ID_TRANSITION, data: GLOBAL_ID_TRANSITION_CLEAR });
            dispatch({ type: 'USER_CONTACT_VERIFIED', data: false });
            dispatch({ type: 'USER_PROFILE', data: {} });
            // Nothing else resets this, so the next user on the same browser session would
            // otherwise see the previous user's 2FA email pre-filled on the register page.
            dispatch({ type: 'USER_GET_TFA_DETAILS_DATA', data: {} });
            dispatch({ type: CONTINUE_WITH_APPID_CLICK, data: '' });
            dispatch({ type: AUTH_SESSION, data: { authenticatedUser: {}, isAuthenticated: false, isLauncherAuth: false } });
            dispatch({ type: USER_INPUT_PASSWORD_CHANGE, data: '' });
            dispatch({ type: USER_INPUT_TFA_PASSCODE_CHANGE, data: '' });
            if (rememberMeValue === true) {
                dispatch({ type: USER_INPUT_USER_NAME_CHANGE, data: currentUserName });
            }
            if (typeof doClear == 'undefined' || doClear === true) {
                if (typeof window.jspLoginApp !== 'undefined' && window.jspLoginApp != 'LAUNCHER') {
                    window.jspLoginApp = '';
                }
                if (typeof window.jspApp !== 'undefined') {
                    window.jspApp = '';
                }
            }
            if (stayPage === true) return;
            hashHistory.push(constants.ROUTE_PAGE_LOGIN);
        };

        apiPostAsync(constants.API_LOGOUT_USERTOKEN, null, postData)
            .then(clearAuthAndRedirect)
            .catch(clearAuthAndRedirect);
    }
}

export async function authenticateTfaAsync(userName, appKey, password, tfaPasscode, webauthnCredential) {
    const postData = {
        userId: userName,
        globalId: userName,
        password: password,
        appKey: appKey,
    };
    // No rememberUserId — this is the TFA-update re-auth, not a login. Sending 'N' made the
    // server delete the iceSsoGid cookie and silently un-remember the user.
    let permissions = appKey;
    if (JspUtil.isValueAvailable(window.jspPermissions)) {
        permissions = jspPermissions;
    }
    postData.permissions = permissions + ',launcher';
    if (tfaPasscode) {
        postData.otpCode = tfaPasscode;
    }
    if (webauthnCredential) {
        postData.webauthnRequestId = webauthnCredential.requestId;
        postData.webauthnPublicKey = webauthnCredential.publicKey;
        postData.webauthnUserName = webauthnCredential.userName;
        postData.webauthnApp = webauthnCredential.appKey;
    }
    const apiName = appKey === constants.APP_KEY_GLOBAL
        ? constants.API_AUTHENTICATE_GLOBAL
        : constants.API_AUTHENTICATE_TFA;
    return apiPostAsync(apiName, null, postData);
}

export async function authAndSendCodeTo2FAEmail(userName, appKey, password, { tfaPasscode, mersOrganization, rememberMe, appUserLoginTfa, loginApp } = {}) {
    if (mersOrganization && userName.indexOf('@') === -1) {
        userName = mersOrganization + '_' + userName;
    }
    // Email-based logins are Global ID users. The server's authAndSendCodeTo2FAEmail path
    // (TfaRestApiUtil.validateUser → readLinkedPrincipals → validatePassword) validates against
    // the per-app password row and has no Global-ID fallback (unlike authenticateTfa's doAuth).
    // After a Global ID password change, the per-app password may be stale — send GLOBAL so the
    // server validates against the authoritative Global ID password.
    if (userName && userName.indexOf('@') !== -1) {
        appKey = constants.APP_KEY_GLOBAL;
    }
    let permissions = appKey;
    if (JspUtil.isValueAvailable(window.jspPermissions)) {
        permissions = window.jspPermissions;
    }
    const postData = {
        userId: userName,
        password,
        appKey,
        rememberUserId: rememberMe ? 'Y' : 'N',
        permissions: permissions + ',launcher',
    };
    if (tfaPasscode) postData.otpCode = tfaPasscode;
    if (appUserLoginTfa) postData.appUserLoginTfa = appUserLoginTfa;
    return apiPostAsync(constants.API_AUTH_AND_SEND_CODE_TO_2FA_EMAIL, null, postData);
}

export async function gotoAppUrl(a, noCredential, userId, appKey, isMemberLogin, globalId) {
    let url = a.url;
    const newWindow = a.newWindow;
    const target = a.name;
    if (a.supportSamlIdpInitiated) {
        submitIdpInitiatedRequest(a, userId, appKey);
        return { sessionExpired: false };
    }
    if (noCredential) {
        gotoApp(url, newWindow, target);
        return { sessionExpired: false };
    }
    try {
        const resp = await apiPostAsync(constants.API_MAKE_CREDENTIALS, null, {
            token: '',
            requests: [{ userId, appKey, permissions: appKey }]
        });
        const data = resp.data;
        if (data && data.length > 0 && data[0].code == 0) {
            url = appendCr(a, url, data[0].credentials, userId, globalId);
        }
        gotoApp(url, newWindow, target);
    } catch (resp) {
        if (resp.code == errorCodes.ERR_PLEASE_LOGIN && !isMemberLogin) {
            return { sessionExpired: true };
        }
        gotoApp(url, newWindow, target);
    }
    return { sessionExpired: false };
}

function submitIdpInitiatedRequest(a, userId, appKey) {
    let form = document.createElement('form');
    form.method = 'POST';
    form.action = '/appUserLogin';

    form.appendChild(createHiddenInputElement('loginUserId', userId));
    form.appendChild(createHiddenInputElement('loginApp', appKey));
    form.appendChild(createHiddenInputElement('samlIdpInitiated', 'Y'));
    form.appendChild(createHiddenInputElement('protectedUrl', a.url));
    form.appendChild(createHiddenInputElement('nologin', 'Y'));
    document.body.appendChild(form);

    console.log('Initiated IdP flow for app=' + appKey);
    form.submit();
}

function createHiddenInputElement(name, value) {
    let nologinHidden = document.createElement('input');
    nologinHidden.type = 'hidden';
    nologinHidden.name = name
    nologinHidden.value = value;
    return nologinHidden;
}
export async function gotoAppWithUser(a, userId, appKey, globalId) {
    let url = a.url;
    const newWindow = a.newWindow;
    const target = a.name;
    try {
        const resp = await apiPostAsync(constants.API_MAKE_CREDENTIALS, null, {
            token: '',
            requests: [{ userId, appKey, permissions: appKey }]
        });
        const data = resp.data;
        if (data && data.length > 0 && data[0].code == 0) {
            url = appendCr(a, url, data[0].credentials, userId, globalId);
        }
    } catch {
        // on error, navigate to app without credentials
    }
    gotoApp(url, newWindow, target);
}

export async function upgradeSession(userId, appKey, otpCode) {
    return apiPostAsync(constants.API_UPGRADE_SESSION, null, { userId, appKey, otpCode });
}

export async function sendUpgradeCode(userId, appKey) {
    return apiPostAsync(constants.API_SEND_UPGRADE_CODE, null, { userId, appKey });
}

export async function saveUserProfile(userProfile) {
    return apiPostAsync(constants.API_SAVE_USER_PROFILE, null, userProfile);
}

export async function loadUserProfile(userName, appKey) {
    const resp = await apiPostAsync(constants.API_LOAD_USER_PROFILE, null, { userId: userName, appKey });
    return { ...resp, data: { ...resp.data, userId: userName, appKey } };
}

export async function getUserAppConfigs(userName, app, isFederated, isMortgage) {
    if (typeof isFederated === 'undefined') {
        isFederated = typeof window.jspImtFederated !== 'undefined' && !!window.jspImtFederated;
    }
    if (typeof isMortgage === 'undefined') {
        isMortgage = typeof window.jspIsMortgage !== 'undefined' && !!window.jspIsMortgage;
    }

    let mortgageUserName = null;
    if (JspUtil.isValueAvailable(window.jspImtUserName)) {
        sessionStorage.setItem('imtUserName', window.jspImtUserName);
        mortgageUserName = window.jspImtUserName;
    } else {
        mortgageUserName = sessionStorage.getItem('imtUserName');
    }

    const resp = await apiPostAsync(constants.API_GET_USER_APP_CONFIGS, null, {
        userId: isMortgage && mortgageUserName ? mortgageUserName : userName,
        appKey: isMortgage ? 'BK_SSO' : app,
        isFederated: isFederated ? 'Y' : 'N',
        isMortgage: isMortgage ? 'Y' : 'N'
    });
    const config = {};
    if (resp.data) {
        if (resp.data.favoriteApps) config.favoriteApps = JSON.parse(resp.data.favoriteApps);
        if (resp.data.dashboardConfig) {
            const dc = JSON.parse(resp.data.dashboardConfig);
            if (dc && dc.systemGroupConfig) config.dashboardConfig = dc.systemGroupConfig;
        }
        if (resp.data.memberLoginApps) config.appConfigs = JSON.parse(resp.data.memberLoginApps);
        if (resp.data.pageConfig) config.pageConfig = JSON.parse(resp.data.pageConfig);
    }
    return config;
}


export async function updateUserFavorites(userName, app, favs) {
    const postData = {
        userId: userName,
        appKey: app,
        prefValue: JSON.stringify(favs),
        prefKey: "LOGIN_PAGE_FAVORITE_APPS"
    };
    return apiPostAsync(constants.API_SAVE_USER_PREFERENCE, null, postData);
}


export function processReferenceData(data) {
    // The API already returns countries with nested stateCodes and jurisdictions,
    // so we keep the raw array as the single source of truth and add a countryByCode
    // lookup map for O(1) access. Consumers derive area codes / dropdown options inline.
    const countries = (data && data.countries) || [];
    const countryByCode = {};
    for (const c of countries) {
        countryByCode[c.countryCode] = c;
    }
    return { countries, countryByCode };
}

export function getReferenceData(userName, app) {
    return function (dispatch) {
        apiPostAsync(constants.API_GET_REFERENCE_DATA, null, {
            userId: userName || ' ',
            appKey: app || ' '
        }).then(resp => {
            if (resp.data) {
                dispatch({ type: REFERENCE_DATA, data: processReferenceData(resp.data) });
            }
        }).catch(err => console.error('getReferenceData failed:', err));
    }
}

export function logUserConnection(settings, permissions, authData, testResult) {
    return function (dispatch) {
        if (!checkUserConnection(settings)) {
            return;
        }
        if (testResult) {
            for (let url in testResult) {
                sendUserConnection(permissions, authData, testResult[url]);
            }
        }
    }
}

export async function sendPhoneVerificationCode(userId, appKey, phone, captchaResponse) {
    const postData = { ...phone, captchaResponse, userId, userName: userId, appKey };
    return apiPostAsync(constants.API_SEND_PHONE_VERIFICATION_CODE, null, postData);
}

export async function confirmPhone(userId, appKey, userContact, phoneData) {
    const postData = { ...userContact, ...phoneData, userId, userName: userId, appKey };
    const resp = await apiPostAsync(constants.API_CONFIRM_PHONE, null, postData);
    return { ...resp, data: { ...resp.data, userId, appKey } };
}

function openEulaPage(eulaDoc, dispatch) {
    hashHistory.push(constants.ROUTE_PAGE_EULA);
}

function gotoApp(url, newWindow, target) {
    if (JspUtil.isValueAvailable(window.jspRefererDomain)) {
        url = replaceDomain(url, window.jspRefererDomain);
    }
    if (newWindow) {
        window.open(url, target);
    }
    else {
        window.location.href = url;
    }
}

function replaceDomain(url, domain) {
    const reIce = /http.*?\.ice.com/i
    if (url && url.match(reIce)) {
        const authFilterRedirectUrl = "authFilterRedirectUrl";
        if (url.indexOf(authFilterRedirectUrl) > 0) {
            return url.replace(/\.ice.com/gi, domain);
        }
        return url.replace(/\.ice.com/i, domain);
    }
    return url;
}

function appendCr(a, url, cr, userId, globalId) {
    let u = new URL(url);
    if (a.credentialParam) {
        u.searchParams.set(a.credentialParam, cr);
    }
    else {
        u.searchParams.set('credentials', cr);
    }
    if (a.userParam) {
        u.searchParams.set(a.userParam, userId);
    }
    else {
        u.searchParams.set('userId', userId);
    }
    if (a.iws && globalId) {
        u.searchParams.set('globalId', globalId);
    }
    u.searchParams.set('sourceApp', 'SSO');
    return u.toString();
}

function loginTransitionGlobal(dispatch, transData) {
    // `remainingAttempts == 0` is the trigger, so it cannot be guarded by a
    // truthiness check — both 0 and "0" are falsy/valid respectively.
    if (transData.remainingAttempts != null && transData.remainingAttempts !== '' && transData.remainingAttempts == 0) {
        if (transData.email) {
            dispatch({ type: GLOBAL_ID_TRANSITION, data: { ...transData, errTransition: false, errAttemptsExceeded: true } });
            dispatch({ type: USER_INPUT_USER_NAME_CHANGE, data: transData.email });
            dispatch({ type: USER_INPUT_PASSWORD_CHANGE, data: '' });
            return true;
        }
    }
    return false;
}

function checkUserConnection(settings) {
    if (settings && settings.urls) {
        const host = window.location.hostname;
        if (host != settings.host) {
            return false;
        }
        if (settings.noLauncher && settings.noLauncher === 'Y') {
            if (JspUtil.isValueAvailable(window.jspLoginApp) && window.jspLoginApp == constants.APP_LAUNCHER) {
                return false;
            }
        }
        return true;
    }
    return false;
}

function sendUserConnection(permissions, authData, testResult) {
    const postData = {
        userId: authData.user,
        appKey: authData.app,
        permissions,
        appCompanyId: authData.appCompanyId,
        appCompanyName: authData.appCompanyName,
        authStart: authData.authStart + '',
        authEnd: authData.authEnd + '',
        pingStart: testResult.pingStart + '',
        pingEnd: testResult.pingEnd + '',
        url: testResult.url,
        err: testResult.err,
        status: testResult.status,
    };
    apiPostAsync(constants.API_LOG_USER_CONNECTION, null, postData)
        .catch(() => console.error('logUserConnection failed'));
}

export function saveAuthParam(userName, appKey, loginApp, password, passwordLessOtp, captchaResponse, tfaPasscode, mersOrganization, rememberMe, webauthnCredential, useRegisteredPasskey, appUserLoginTfa) {
    return { userName, appKey, loginApp, password, passwordLessOtp, captchaResponse, tfaPasscode, mersOrganization, rememberMe, webauthnCredential, useRegisteredPasskey, appUserLoginTfa };
}


export function handlePasswordLessSAMLResp(dispatch, resp) {
    console.log('handlePasswordLessSAMLResp resp=' + resp);
    if(resp && resp.data) {
        console.log('handlePasswordLessSAMLResp data=' + resp.data);
        dispatch({ type: PASSWORD_LESS_SAML_CONFIG, data: resp.data });
        hashHistory.push(constants.ROUTE_PAGE_PASSWORD_LESS_SAML_SUBMIT);
    }
}

export function handlePasswordLessOTPResp(dispatch) {
    dispatch({type: USER_INPUT_PASSWORD_LESS_OTP_CHANGE, data: ''});
    dispatch({type: USER_INPUT_CAPTCHA_RESPONSE, data: ''});
    dispatch({type: AUTH_MODE_STATE, data: { requiresPasswordLessOtp: true, requiresPassword: false }});
}

export function handlePasswordRequiredResp(dispatch) {
    dispatch({type: USER_INPUT_PASSWORD_LESS_OTP_CHANGE, data: ''});
    dispatch({type: USER_INPUT_CAPTCHA_RESPONSE, data: ''});
    dispatch({type: AUTH_MODE_STATE, data: { requiresPasswordLessOtp: false, requiresPassword: true }});
}

export async function sendOtpCodeToEmail(emailAddress, appKey, isNewEmail, userName) {
    const postData = isNewEmail
        ? { newEmail: emailAddress, appKey, userName }
        : { email: emailAddress, appKey, userName };
    return apiPostAsync(constants.API_SEND_OTP_CODE_TO_EMAIL, null, postData);
}

export async function verifyEmailConfirmationCode(emailAddress, confirmationCode, userName) {
    return apiPostAsync(constants.API_VERIFY_EMAIL_OTP_CODE, null, {
        emailAddress, appKey: 'GLOBAL', confirmationCode, userName
    });
}

export async function changeGlobalIdEmailValidateDomain(newEmail, userName) {
    return apiPostAsync(constants.API_CHANGE_GLOBAL_ID_EMAIL_VALIDATE_DOMAIN, null, {
        newEmail, appKey: 'GLOBAL', userName
    });
}
